/** Response Roster / People tool (Page183 Soft≠Live).
 * Live-first: drag → POST /responder-dispatches onto Live devices.
 * Soft practice is explicit only — never auto-degrade when Live is empty.
 */
import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { html, Ico, Alert, Ticker, stay } from "./ui.js";
import { api, path, useApi } from "./api.js";
import { isLocalHost } from "./pathseal.js";
import { useEventsLive } from "./stream-live.js";
import {
  personnelRosterOf,
  softResponseErrorOf,
} from "./response-live.js";
import {
  softDevicesClone,
  softDeviceMarksOf,
} from "./response-soft-demo.js";
import {
  softPeopleRowsOf,
  softStampDispatch,
  softSyncSubscribe,
  softTaskChipForDevice,
} from "./response-soft-sync.js";
import { applyPeopleRegisterOverlay, peopleRegisterSubscribe } from "./resources-people-registry.js";
import { boardHeadingOf } from "./operator-say.js";
import {
  assignDevicesWithFixOf,
  responseAssignSeatOf,
} from "./response-assign.js";
export { softResponseErrorOf };

function safeRosterPath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__response_roster_status") return "/response/roster/status";
    if (op === "get__personnel") {
      const lim = (opts && opts.query && opts.query.limit) || 200;
      return `/personnel?limit=${encodeURIComponent(lim)}`;
    }
    if (op === "get__personnel_policy") return "/personnel/policy";
    if (op === "get__response_assign_status") return "/response/assign/status";
    if (op === "get__devices") {
      const lim = (opts && opts.query && opts.query.limit) || 80;
      return `/devices?limit=${encodeURIComponent(lim)}`;
    }
    if (op === "post__responder-dispatches") return "/responder-dispatches";
    return "";
  }
}

/** Pure: seat chips from Live roster posture — fail-closed. */
export function responseRosterSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const max = Number(lim.roster_max);
  const def = Number(lim.roster_default);
  return {
    rosterAllowed: p.roster_allowed === true,
    policyAllowed: p.policy_allowed === true,
    reReadAllowed: p.re_read_allowed === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    enabled: p.enabled === true,
    rosterLimit:
      Number.isFinite(def) && def > 0
        ? Math.min(def, Number.isFinite(max) && max > 0 ? max : 500)
        : 200,
  };
}

/** Pure: clamp Live personnel paint — no Soft invent. */
export function rosterRowsClampOf(rows, maxN) {
  const list = Array.isArray(rows) ? rows : [];
  const n = Number(maxN);
  const cap = Number.isFinite(n) && n > 0 ? Math.floor(n) : 200;
  return list.slice(0, cap);
}

/** Pure: Live drop targets from GET /devices (prefer fix; fall back to register). */
export function rosterLiveDropDevicesOf(pack, maxN) {
  const withFix = assignDevicesWithFixOf(pack, maxN);
  if (withFix.length) return withFix;
  const root = pack && typeof pack === "object" ? pack : {};
  const items = root.items || root.devices || [];
  const rows = Array.isArray(items) ? items : [];
  const n = Number(maxN);
  const cap = Number.isFinite(n) && n > 0 ? Math.floor(n) : 80;
  return rows
    .filter((d) => d && String(d.device_id || d.id || "").trim())
    .slice(0, cap);
}

const FILTERS = [
  { id: "all", label: "All" },
  { id: "on", label: "On call" },
  { id: "ready", label: "Consented" },
];

export function Roster({ toast, mode = "response" }) {
  const registerOnly = mode === "resources";
  const [forceSoft, setForceSoft] = useState(false);
  const useSoft = registerOnly ? false : (isLocalHost() && forceSoft === true);

  const posture = useApi(safeRosterPath("get__response_roster_status"));
  const post = posture.data || {};
  const seat = responseRosterSeatOf(posture.loading ? null : post);
  const assignPosture = useApi(
    registerOnly ? "" : safeRosterPath("get__response_assign_status"),
  );
  const assignSeat = responseAssignSeatOf(
    assignPosture.loading ? null : (assignPosture.data || {}),
  );
  const live = useApi(
    seat.rosterAllowed
      ? safeRosterPath("get__personnel", { query: { limit: seat.rosterLimit } })
      : "",
  );
  const policyLive = useApi(
    seat.policyAllowed
      ? (safeRosterPath("get__personnel_policy") || "/personnel/policy")
      : "",
  );
  const devicesLive = useApi(
    !registerOnly && !useSoft && assignSeat.registerAllowed
      ? safeRosterPath("get__devices", { query: { limit: assignSeat.deviceLimit } })
      : "",
  );
  const dispatchEvents = useEventsLive(15000);
  const rawRows = useMemo(() => personnelRosterOf(live.data), [live.data]);
  const posSrc = (!useSoft && live.data && live.data.sources && live.data.sources.position) || null;
  const sitePosDark = !!(posSrc && posSrc.live === false);
  const [regTick, setRegTick] = useState(0);
  useEffect(() => peopleRegisterSubscribe(() => setRegTick((n) => n + 1)), []);
  const liveRows = useMemo(
    () => applyPeopleRegisterOverlay(rosterRowsClampOf(rawRows, seat.rosterLimit), { soft: false }),
    [rawRows, seat.rosterLimit, regTick],
  );
  const softDevices = useMemo(() => softDevicesClone(), []);
  const dropGridRef = useRef(null);
  const dropScrollRef = useRef({ active: false, vy: 0, raf: 0 });
  const [softPeople, setSoftPeople] = useState(() => softPeopleRowsOf());
  const [softTick, setSoftTick] = useState(0);
  const [busyDrop, setBusyDrop] = useState(false);
  const rows = useSoft ? softPeople : liveRows;
  const liveDropDevices = useMemo(
    () => rosterLiveDropDevicesOf(devicesLive.data, assignSeat.deviceLimit),
    [devicesLive.data, assignSeat.deviceLimit],
  );
  const dropDevices = useSoft ? softDevices : liveDropDevices;
  const canLiveDrag = !registerOnly && !useSoft && assignSeat.createAllowed === true;
  /* Drag rail stays visible whenever Soft practice OR Live create is armed —
     do not hide grips/dock until a drag starts (operators never discover it).
     Resources mode is register-only — no drag-dispatch (Response owns send). */
  const dragRail = registerOnly ? false : (useSoft || canLiveDrag);
  const dockOpen = dragRail;

  useEffect(() => softSyncSubscribe((snap) => {
    if (snap && Array.isArray(snap.people)) setSoftPeople(snap.people.map((r) => ({ ...r })));
    setSoftTick((n) => n + 1);
  }), []);

  const liveDispatchSeen = useRef("");
  useEffect(() => {
    const events = dispatchEvents.data && Array.isArray(dispatchEvents.data.items)
      ? dispatchEvents.data.items
      : [];
    const hit = events.find((ev) =>
      String(ev && ev.event_type || "").startsWith("responder.dispatch."),
    );
    const eid = hit ? String(hit.id || hit.event_id || hit.ts_epoch_ms || "") : "";
    if (!eid || eid === liveDispatchSeen.current) return;
    liveDispatchSeen.current = eid;
    if (!forceSoft) {
      live.reload && live.reload();
      devicesLive.reload && devicesLive.reload();
    }
  }, [dispatchEvents.data, forceSoft, live.reload, devicesLive.reload]);

  const policy = (!useSoft && policyLive.data && typeof policyLive.data === "object")
    ? policyLive.data
    : null;
  const active = rows.filter((r) => r.status === "active");
  const onCall = active.filter((r) => r.on);
  const thrS = policy && Number(policy.stale_threshold_seconds) > 0
    ? Number(policy.stale_threshold_seconds)
    : null;
  const thrLabel = thrS != null
    ? (thrS >= 60
      ? (Math.round(thrS / 60 * 10) / 10) + " min"
      : thrS + " s")
    : "—";
  const freshOnly = seat.policyAllowed && policy
    ? (policy.fresh_only_dispatch ? "on" : "off")
    : "—";

  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [openUser, setOpenUser] = useState("");
  const [dragUser, setDragUser] = useState("");
  const [dropHot, setDropHot] = useState("");

  const stopDropScroll = useCallback(() => {
    const st = dropScrollRef.current;
    st.active = false;
    st.vy = 0;
    if (st.raf) {
      cancelAnimationFrame(st.raf);
      st.raf = 0;
    }
  }, []);

  const tickDropScroll = useCallback(() => {
    const el = dropGridRef.current;
    const st = dropScrollRef.current;
    if (!el || !st.active) {
      st.raf = 0;
      return;
    }
    if (st.vy) el.scrollTop += st.vy;
    st.raf = requestAnimationFrame(tickDropScroll);
  }, []);

  /** Anywhere over the drop zone: map Y → smooth scroll velocity (not edge-only). */
  const scrollDropWhileDrag = useCallback((e) => {
    e.preventDefault();
    try { e.dataTransfer.dropEffect = "copy"; } catch (_) { /* ignore */ }
    const el = dropGridRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const h = Math.max(rect.height, 1);
    const t = Math.min(1, Math.max(0, (e.clientY - rect.top) / h));
    let vy = 0;
    if (t < 0.48) {
      const u = (0.48 - t) / 0.48;
      vy = -(2 + u * u * 26);
    } else if (t > 0.52) {
      const u = (t - 0.52) / 0.48;
      vy = 2 + u * u * 26;
    }
    const st = dropScrollRef.current;
    st.vy = vy;
    st.active = true;
    if (!st.raf) st.raf = requestAnimationFrame(tickDropScroll);
  }, [tickDropScroll]);

  const endDragDispatch = useCallback(() => {
    setDragUser("");
    setDropHot("");
    stopDropScroll();
  }, [stopDropScroll]);

  const shown = useMemo(() => {
    const needle = String(q || "").trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === "on" && !(r.status === "active" && r.on)) return false;
      if (filter === "ready" && r.status !== "active") return false;
      if (!needle) return true;
      const hay = (
        String(r.name || "") + " " + String(r.user || "") + " " + String(r.phone || "")
      ).toLowerCase();
      return hay.includes(needle);
    });
  }, [rows, filter, q]);

  const reRead = () => {
    posture.reload && posture.reload();
    live.reload && live.reload();
    assignPosture.reload && assignPosture.reload();
    devicesLive.reload && devicesLive.reload();
    if (seat.policyAllowed) policyLive.reload && policyLive.reload();
    toast(useSoft ? "Soft people ready" : "People list refreshed", "go");
  };

  const softDrop = (deviceId, person) => {
    if (!useSoft || !person) return;
    const stamp = softStampDispatch({
      deviceId,
      units: [{
        id: String(person.user || person.id || ""),
        label: String(person.name || person.user || ""),
        kind: "human",
      }],
      source: "people-drag",
    });
    if (!stamp || !stamp.ok) {
      toast("Soft drop needs an on-call person and a Soft device", "warn");
      return;
    }
    toast(
      "Soft → task " + stamp.taskLabel + " · " + stamp.queueId
        + " · " + stamp.who + " · Queue + Map Soft sync · not Live",
      "warn",
    );
    stay(
      "/response/board?focus=" + encodeURIComponent(stamp.queueId)
        + "&return_to=" + encodeURIComponent("/response/roster"),
    );
  };

  const liveDrop = async (deviceId, person) => {
    if (useSoft || !person) return;
    if (!person.on) {
      toast("Drop an on-call person", "warn");
      return;
    }
    if (!canLiveDrag) {
      toast("Live drag-dispatch needs a manager seat (Assign create)", "warn");
      return;
    }
    const did = String(deviceId || "").trim();
    if (!did) {
      toast("Pick a Live device target", "warn");
      return;
    }
    setBusyDrop(true);
    try {
      const out = await api(safeRosterPath("post__responder-dispatches"), {
        method: "POST",
        body: {
          device_id: did,
          responder_username: String(person.user || ""),
          reason: "CMC People drag-dispatch",
          risk_score: 0,
        },
      });
      const rec = out && out.record;
      const task = String((rec && (rec.task_id || rec.dispatch_id || rec.id)) || "").trim();
      toast(
        "Live send · " + (person.name || person.user)
          + " → " + boardHeadingOf(did),
        "go",
      );
      stay(
        "/response/board"
          + (task ? ("?focus=" + encodeURIComponent(task)) : "")
          + "&return_to=" + encodeURIComponent("/response/roster"),
      );
    } catch (e) {
      toast(softResponseErrorOf((e && e.message) || e || "Dispatch refused"), "bad");
    } finally {
      setBusyDrop(false);
      endDragDispatch();
    }
  };

  const onDropTarget = (deviceId, person) => {
    if (useSoft) softDrop(deviceId, person);
    else void liveDrop(deviceId, person);
  };

  const dragEnabled = (r) => !!(r && r.on && (useSoft || canLiveDrag));

  return html`
    <div className="rr-people" data-cmc-residual="page183-roster" data-soft=${useSoft ? "1" : "0"}
      data-dragging=${dragUser ? "1" : "0"} data-live-drag=${canLiveDrag ? "1" : "0"}
      data-rail=${dragRail ? "1" : "0"} data-resources-mode=${registerOnly ? "1" : "0"}>
      <p className="rr-people__lede">
        ${registerOnly
          ? "Resources register · membership, consent, and on-call. Drag-send stays in Response."
          : useSoft
            ? "Practice · left ≡ grip → right practice devices. Drops stay on this sheet."
            : canLiveDrag
              ? "Live · left ≡ grip → right Live devices. Drop sends a live dispatch."
              : "Live roster · drag needs an on-call person with consent."}
      </p>
      ${registerOnly ? null : isLocalHost() ? html`<div className="rr-people__mode">
        <button type="button" data-on=${useSoft ? "1" : "0"}
          onClick=${() => setForceSoft(true)}>Soft practice</button>
        <button type="button" data-on=${!useSoft ? "1" : "0"}
          onClick=${() => setForceSoft(false)}>Live roster (${liveRows.length})</button>
      </div>` : null}
      ${dragRail ? html`
        <p className="rr-people__howto" data-cmc-howto="people-drag">
          <b>Drag here</b>
          <span>Pull the ≡ handle on a person, drop on a device card on the right.</span>
          <em>${onCall.length
            ? (onCall.length + " on-call ready")
            : (useSoft ? "Soft people include on-call rows" : "Nobody on-call yet — grips stay visible but drop needs on-call")}</em>
        </p>` : null}

      ${!posture.loading && !seat.enabled
        ? html`<${Alert} tone="warn" title="People closed" say=${softResponseErrorOf(seat.detail || "Roster closed")}/>`
        : null}
      ${live.error ? html`<${Alert} tone="warn" title="Could not load people" say=${softResponseErrorOf(live.error)}/>` : null}
      ${sitePosDark ? html`<${Alert} tone="warn" title="Site locations blocked"
          say=${softResponseErrorOf((posSrc && posSrc.reason) || "site_membership_required")}/>` : null}
      ${policyLive.error ? html`<${Alert} tone="warn" title="Freshness policy unavailable" say=${softResponseErrorOf(policyLive.error)}/>` : null}
      ${!useSoft && devicesLive.error
        ? html`<${Alert} tone="warn" title="Could not load devices" say=${softResponseErrorOf(devicesLive.error)}/>`
        : null}
      ${!registerOnly && !useSoft && !assignPosture.loading && !canLiveDrag
        ? html`<${Alert} tone="info" title="Drag-dispatch sealed"
            say="This seat can read People but Live drop needs Assign create."/>`
        : null}
      ${registerOnly
        ? html`<${Alert} tone="info" title="Register only"
            say="This seat lists members for Resources. Open Response to Assign or push Queue."/>`
        : null}

      <div className="rr-people__kpis" role="group" aria-label="People summary">
        <div>
          <span>On call</span>
          <b><${Ticker} value=${(useSoft || seat.rosterAllowed) ? onCall.length : 0}/></b>
          <em>${registerOnly ? "listed on-call" : "ready to send"}</em>
        </div>
        <div>
          <span>Consented</span>
          <b><${Ticker} value=${(useSoft || seat.rosterAllowed) ? active.length : 0}/></b>
          <em>sharing location</em>
        </div>
        <div>
          <span>Listed</span>
          <b><${Ticker} value=${(useSoft || seat.rosterAllowed) ? rows.length : 0}/></b>
          <em>${useSoft ? "Soft practice" : "on this seat"}</em>
        </div>
      </div>

      <p className="rr-people__fresh" data-cmc-policy="freshness">
        <span>Freshness policy</span>
        ${useSoft
          ? html`<b>Soft preview</b><em>· not Live policy</em>`
          : !seat.policyAllowed
            ? html`<b>sealed</b>`
            : policyLive.loading && !policy
              ? html`<b>…</b>`
              : html`<b>stale after ${thrLabel}</b><em>· fresh-only dispatch ${freshOnly}</em>`}
      </p>

      <div className="rr-people__tools">
        <div className="rr-people__filters" role="tablist" aria-label="Filter people">
          ${FILTERS.map((f) => html`
            <button key=${f.id} type="button" role="tab"
              aria-selected=${filter === f.id ? "true" : "false"}
              data-on=${filter === f.id ? "1" : "0"}
              disabled=${!useSoft && !seat.rosterAllowed}
              onClick=${() => setFilter(f.id)}>${f.label}</button>`)}
        </div>
        <input className="inp rr-people__search" type="search"
          placeholder="Search name or phone…"
          aria-label="Search people"
          value=${q}
          disabled=${!useSoft && !seat.rosterAllowed}
          onChange=${(e) => setQ(e.target.value)}/>
      </div>

      <div className="rr-people__stage">
        <div className="rr-people__list" role="list" aria-label="People">
          ${!useSoft && !seat.rosterAllowed
            ? html`<p className="note">People list is sealed for this account.</p>`
            : !rows.length
              ? html`<p className="note">${live.loading || posture.loading ? "Loading people…" : "No people on the Live roster for this site. Soft auto-fill is forbidden."}</p>`
              : !shown.length
                ? html`<p className="note">No matches for this filter.</p>`
                : shown.map((r) => {
                  const open = openUser === r.user;
                  const consentLabel = r.status === "active" ? "consented" : (r.status === "revoked" ? "revoked" : "no consent");
                  const canDrag = dragEnabled(r);
                  return html`
                    <article key=${r.user} className="rr-people__row" role="listitem"
                      data-on=${r.on ? "1" : "0"} data-ready=${r.status === "active" ? "1" : "0"}
                      data-open=${open ? "1" : "0"}
                      data-dragging=${dragUser === r.user ? "1" : "0"}
                      data-draggable=${canDrag ? "1" : "0"}>
                      ${dragRail ? html`
                        <span className="rr-people__grip"
                          draggable=${canDrag ? "true" : "false"}
                          data-armed=${canDrag ? "1" : "0"}
                          title=${canDrag
                            ? (useSoft ? "Drag to Soft dispatch" : "Drag to Live dispatch")
                            : "Set on-call on the phone app to enable drag"}
                          aria-label=${canDrag
                            ? ("Drag " + r.name + (useSoft ? " to Soft dispatch" : " to Live dispatch"))
                            : (r.name + " · not on-call")}
                          onDragStart=${(e) => {
                            if (!canDrag) {
                              e.preventDefault();
                              toast("Only on-call people can be dragged", "warn");
                              return;
                            }
                            setDragUser(r.user);
                            setDropHot("");
                            try {
                              e.dataTransfer.setData("text/plain", r.user);
                              e.dataTransfer.effectAllowed = "copy";
                            } catch (_) { /* ignore */ }
                          }}
                          onDragEnd=${() => { endDragDispatch(); }}>
                          <i/><i/><i/>
                        </span>` : null}
                      <button type="button" className="rr-people__main"
                        onClick=${() => setOpenUser(open ? "" : r.user)}>
                        <span className="rr-people__who">
                          <b>${r.name}${r.registerProfileLive ? html` <em className="mux__soft-tag" title="Live register profile SoR">Live</em>` : null}</b>
                          <em>${r.callsign ? r.callsign + " · " : ""}${r.crew ? r.crew + " · " : ""}${r.user}${r.role && r.role !== "—" ? (" · " + r.role) : ""}</em>
                          <strong className="rr-people__phone" data-empty=${r.phone ? "0" : "1"}>
                            ${r.phoneSay || "No phone on file"}
                          </strong>
                        </span>
                        <span className="rr-people__chips">
                          <span className="chip" data-tone=${r.status === "active" ? "" : "warn"}>${consentLabel}</span>
                          <span className="chip" data-tone=${r.on ? "" : "off"}>${r.on ? "on call" : "off call"}</span>
                          ${canDrag ? html`<span className="chip" data-tone="ai">drag →</span>`
                            : (dragRail ? html`<span className="chip" data-tone="off">no drag</span>` : null)}
                        </span>
                      </button>
                      ${open ? html`
                        <div className="rr-people__detail" data-open="1">
                          ${[
                            ["Phone", r.phoneSay || "No phone on file"],
                            ["Username", r.user],
                            ["Display name", r.name],
                            ["Callsign", r.callsign || "—"],
                            ["Crew", r.crew || "—"],
                            ["Register SoR", r.registerProfileLive ? "Live profile on server" : "Username only"],
                            ["Role", r.role || "—"],
                            ["Site / tenant", r.site],
                            ["Consent", consentLabel],
                            ["On call", r.on ? "yes · ready to send" : "no"],
                            ["Consent granted", r.granted],
                            ["Retention", r.ret === "—" ? "—" : (String(r.ret) + " days")],
                            ["Accuracy tier", r.tier],
                            ["Consent version", r.ver],
                            ["Live position", r.positionSay || "No live fix"],
                            ["Current assignment", r.assignmentSay || "Not on a Live job"],
                            ["Source", r.soft_demo ? "Soft practice · not Live" : "Live roster"],
                          ].map(([k, v]) => html`
                            <div key=${k} className="rr-people__field">
                              <u>${k}</u>
                              <b>${v}</b>
                            </div>`)}
                        </div>
                        <div className="rr-people__row-acts">
                          <span className="note">${canDrag
                            ? "Drag this card — device targets slide in beside the list."
                            : (r.on
                              ? "On call · Live drop sealed for this seat."
                              : "Set on-call on the phone app to enable drag.")}</span>
                          ${r.phone ? html`<a className="btn btn--ghost btn--sm" href=${"tel:" + String(r.phone).replace(/\s+/g, "")}>Call</a>` : null}
                        </div>` : null}
                    </article>`;
                })}
        </div>

        ${dragRail ? html`<aside className="rr-people__dock" data-open=${dockOpen ? "1" : "0"}
          data-many=${dropDevices.length > 10 ? "1" : "0"}
          data-hot=${dragUser ? "1" : "0"}
          aria-hidden=${dockOpen ? "false" : "true"}
          aria-label=${useSoft ? "Soft drag-dispatch targets" : "Live drag-dispatch targets"}
          onDragOver=${scrollDropWhileDrag}
          onDragEnter=${(e) => { e.preventDefault(); }}
          onWheel=${(e) => {
            const el = dropGridRef.current;
            if (!el) return;
            e.preventDefault();
            el.scrollTop += e.deltaY;
          }}>
          <header>
            <span>Drop zone · ${useSoft ? "Soft devices" : "Live devices"}</span>
            <em>${dragUser
              ? ("Drop " + ((rows.find((r) => r.user === dragUser) || {}).name || dragUser)
                + " · hover dock to scroll · " + dropDevices.length + " targets"
                + (busyDrop ? " · sending…" : ""))
              : (useSoft
                ? ("Ready · " + dropDevices.length + " Soft targets · Soft≠Live")
                : (canLiveDrag
                  ? ("Ready · " + dropDevices.length + " Live targets · POST on drop")
                  : "Live drop sealed · Soft practice optional"))}</em>
          </header>
          <div className="rr-people__drop-grid" ref=${dropGridRef}
            data-count=${dropDevices.length}
            onDragOver=${scrollDropWhileDrag}
            onDragEnter=${(e) => { e.preventDefault(); }}
            onWheel=${(e) => {
              const el = dropGridRef.current;
              if (!el) return;
              e.preventDefault();
              e.stopPropagation();
              el.scrollTop += e.deltaY;
            }}>
            ${!dropDevices.length
              ? html`<p className="note">${useSoft
                ? "No Soft devices."
                : (devicesLive.loading
                  ? "Loading Live devices…"
                  : "No Live devices on this seat — seed devices (Soft auto-fill forbidden).")}</p>`
              : dropDevices.map((d) => {
                const id = String(d.device_id || d.id || "");
                const marks = useSoft
                  ? softDeviceMarksOf(d)
                  : { signal: false, aiControl: "none" };
                const chip = useSoft ? softTaskChipForDevice(id) : null;
                void softTick;
                return html`
                  <button key=${id} type="button" className="rr-people__drop-slot"
                    data-hot=${dropHot === id ? "1" : "0"}
                    data-signal=${marks.signal ? "1" : "0"}
                    data-ai=${marks.aiControl}
                    data-task=${chip ? "1" : "0"}
                    disabled=${busyDrop}
                    title=${chip
                      ? ("Soft task · " + chip.label + " · " + chip.queueId + " · " + chip.status)
                      : (useSoft
                        ? ("Soft device · ad-hoc task on drop · " + id)
                        : ("Live device · POST dispatch on drop · " + id))}
                    onDragOver=${(e) => {
                      e.stopPropagation();
                      setDropHot(id);
                      scrollDropWhileDrag(e);
                    }}
                    onDragEnter=${(e) => { e.preventDefault(); setDropHot(id); }}
                    onDragLeave=${() => setDropHot((h) => (h === id ? "" : h))}
                    onDrop=${(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      stopDropScroll();
                      setDropHot("");
                      let user = dragUser;
                      try { user = e.dataTransfer.getData("text/plain") || user; } catch (_) { /* ignore */ }
                      const person = rows.find((r) => r.user === user);
                      setDragUser("");
                      if (!person || !person.on) {
                        toast("Drop an on-call person", "warn");
                        return;
                      }
                      onDropTarget(id, person);
                    }}
                    onClick=${() => {
                      if (!dragUser) {
                        toast("Drag an on-call person — targets slide in", "warn");
                        return;
                      }
                      const person = rows.find((r) => r.user === dragUser);
                      onDropTarget(id, person);
                    }}>
                    <span className="rr-people__drop-top">
                      <b>${d.display_label || d.label || id}</b>
                      ${marks.signal ? html`<i className="rr-people__sig" title="Active signal" aria-label="Signal"/>` : null}
                    </span>
                    <em>${id}</em>
                    ${chip ? html`
                      <span className="rr-people__task" data-status=${chip.status}>
                        <u>Task</u>
                        <b>${chip.label}</b>
                        <i>${chip.queueId}</i>
                      </span>` : html`
                      <span className="rr-people__task" data-status=${useSoft ? "adhoc" : "live"}>
                        <u>Task</u>
                        <b>${useSoft ? "Ad-hoc on drop" : "Live send on drop"}</b>
                      </span>`}
                    <span className="rr-people__drop-marks">
                      ${marks.signal ? html`<abbr data-k="signal">Signal</abbr>` : null}
                      ${marks.aiControl === "ai" ? html`<abbr data-k="ai">AI</abbr>` : null}
                      ${marks.aiControl === "human" ? html`<abbr data-k="human">Human</abbr>` : null}
                      ${!marks.signal && marks.aiControl === "none" ? html`<abbr data-k="quiet">${useSoft ? "Quiet" : "Live"}</abbr>` : null}
                    </span>
                  </button>`;
              })}
          </div>
        </aside>` : null}
        ${dragRail ? html`<p className="rr-people__dock-hint" data-hide=${dockOpen ? "1" : "0"}>
          Drag on-call · hover dock to scroll · ${useSoft ? "Soft≠Live" : "Live POST syncs Board + Mine"}.
        </p>` : null}
      </div>

      <footer className="rr-people__foot">
        <button type="button" className="btn btn--ghost btn--sm"
          disabled=${!seat.reReadAllowed && !posture.loading}
          onClick=${reRead}>
          <${Ico} d="refresh" w=${13}/> Refresh list
        </button>
        <span className="rr-people__note">Grant / on-call goes to the phone app. Practice rows never send live.</span>
      </footer>
    </div>
  `;
}
